import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import { GraphRequest, GraphResponse, Point, FunctionData } from '../shared';
import * as Icons from './icons';
import { Language, getLanguage, t } from './i18n';
import './styles.css';

// 声明全局 math 对象
declare global {
  interface Window {
    math: any;
    webkit?: {
      messageHandlers?: {
        graphExport?: {
          postMessage: (data: any) => void;
        };
      };
    };
    AndroidInterface?: {
      receiveGraphImage: (base64: string, expression: string) => void;
    };
  }
}

// 网格类型
type GridType = 'none' | 'major' | 'minor' | 'polar' | 'isometric' | 'dots';

const splitInputExpressions = (input: string): string[] => {
  const lines = input.split('\n').map(expr => expr.trim()).filter(Boolean);
  if (lines.length <= 1) return lines;

  const hasParametricX = lines.some(line =>
    (/^x\s*=/.test(line) || /=\s*x$/.test(line)) && /\bt\b/.test(line)
  );
  const hasParametricY = lines.some(line =>
    (/^y\s*=/.test(line) || /=\s*y$/.test(line)) && /\bt\b/.test(line)
  );

  return hasParametricX && hasParametricY ? [lines.join('\n')] : lines;
};

const expressionToLatex = (expression: string): string => {
  const normalizeLine = (line: string) => {
    return line
      .replace(/×/g, '*')
      .replace(/÷/g, '/')
      .replace(/²/g, '^2')
      .replace(/³/g, '^3')
      .replace(/√/g, 'sqrt')
      .replace(/π/g, 'pi')
      .replace(/<=/g, '\\le ')
      .replace(/>=/g, '\\ge ')
      .replace(/!=/g, '\\ne ')
      .replace(/\*/g, '\\cdot ')
      .replace(/\btheta\b/g, '\\theta')
      .replace(/θ/g, '\\theta')
      .replace(/\bpi\b/g, '\\pi')
      .replace(/\bphi\b/g, '\\phi')
      .replace(/\btau\b/g, '\\tau')
      .replace(/\b(sin|cos|tan|cot|sec|csc|sinh|cosh|tanh|ln|log|exp)\s*\(/g, (_match, fn) => `\\${fn}(`)
      .replace(/\b(asin|acos|atan|acot|asec|acsc|arcsin|arccos|arctan|asinh|acosh|atanh|acoth|asech|acsch|sqrt|cbrt|root|abs|sign|sgn|floor|ceil|round|trunc|min|max|clamp|hypot|atan2|frac|gamma|erf|erfc|besselj|sinc|mod|pow|heaviside|step|rect|relu|sigmoid|logistic|softplus|normal)\s*\(/g, (_match, fn) => `\\operatorname{${fn}}(`);
  };

  const lines = expression.split('\n').map(line => normalizeLine(line.trim())).filter(Boolean);
  if (lines.length <= 1) return lines[0] || expression;

  return `\\begin{aligned}${lines.join('\\\\')}\\end{aligned}`;
};

const LatexExpression: React.FC<{ expression: string; className?: string; style?: React.CSSProperties }> = ({
  expression,
  className = '',
  style
}) => {
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    import('katex')
      .then(({ default: katex }) => {
        if (cancelled) return;

        setHtml(katex.renderToString(expressionToLatex(expression), {
          throwOnError: false,
          strict: 'ignore',
          output: 'mathml',
          displayMode: false
        }));
      })
      .catch(() => {
        if (!cancelled) setHtml(null);
      });

    return () => {
      cancelled = true;
    };
  }, [expression]);

  if (!html) {
    return (
      <code className={`latex-expression latex-expression-fallback ${className}`} style={style} title={expression}>
        {expression}
      </code>
    );
  }

  return (
    <span
      className={`latex-expression ${className}`}
      style={style}
      title={expression}
      aria-label={expression}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

// 图标组件包装器
const Icon: React.FC<{ icon: React.ElementType; size?: number; className?: string }> = ({
  icon: IconComponent,
  size = 20,
  className = ''
}) => {
  if (!IconComponent) {
    console.warn('Icon component is undefined');
    return <span className={`icon-fallback ${className}`} style={{ width: size, height: size }}>?</span>;
  }

  return <IconComponent size={size} className={className} />;
};

// GraphCanvas 组件 - 保持原有功能不变
const GraphCanvas: React.FC<{
  functions: FunctionData[],
  viewport: { xMin: number, xMax: number, yMin: number, yMax: number },
  onViewportChange: (viewport: any) => void,
  onExportRequest?: (canvas: HTMLCanvasElement) => void,
  gridType?: GridType
}> = ({ functions, viewport, onViewportChange, onExportRequest, gridType = 'major' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastViewport, setLastViewport] = useState(viewport);
  const [touchDistance, setTouchDistance] = useState(0);

  // 获取设备像素比
  const getPixelRatio = () => {
    return window.devicePixelRatio || 1;
  };

  // 设置高DPI画布
  const setupHighDPICanvas = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return null;

    const rect = container.getBoundingClientRect();
    const pixelRatio = getPixelRatio();

    canvas.width = rect.width * pixelRatio;
    canvas.height = rect.height * pixelRatio;
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(pixelRatio, pixelRatio);
    }

    return { width: rect.width, height: rect.height, ctx };
  };

  // 绘制函数
  const draw = useCallback(() => {
    const result = setupHighDPICanvas();
    if (result && result.ctx) {
      drawGraph(result.ctx, result.width, result.height, viewport);
    }
  }, [functions, viewport, gridType]);

  // 初始化和响应式调整
  useEffect(() => {
    draw();

    const handleResize = () => {
      draw();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [draw]);

  // 注册导出回调
  useEffect(() => {
    if (onExportRequest && canvasRef.current) {
      onExportRequest(canvasRef.current);
    }
  }, [onExportRequest]);

  // 绘制图形 - 修复渲染范围问题
  const drawGraph = (ctx: CanvasRenderingContext2D, width: number, height: number, currentViewport: any) => {
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // 绘制网格
    if (gridType && gridType !== 'none') {
      drawGrid(ctx, width, height, currentViewport);
    }

    drawAxes(ctx, width, height, currentViewport);

    // 绘制所有可见的函数 - 确保渲染整个画布
    functions.forEach((func) => {
      if (func.visible && func.points && func.points.length > 0) {
        drawFunction(ctx, width, height, currentViewport, func);
      }
    });
  };

  // 坐标转换函数 - 使用当前视口直接转换
  const toCanvasX = (x: number, width: number, currentViewport: any) => {
    return ((x - currentViewport.xMin) / (currentViewport.xMax - currentViewport.xMin)) * width;
  };

  const toCanvasY = (y: number, height: number, currentViewport: any) => {
    return height - ((y - currentViewport.yMin) / (currentViewport.yMax - currentViewport.yMin)) * height;
  };

  const toMathX = (canvasX: number, width: number, currentViewport: any) => {
    return currentViewport.xMin + (canvasX / width) * (currentViewport.xMax - currentViewport.xMin);
  };

  const toMathY = (canvasY: number, height: number, currentViewport: any) => {
    return currentViewport.yMax - (canvasY / height) * (currentViewport.yMax - currentViewport.yMin);
  };

  // 获取统一的步长 - 确保1:1比例
  const getUnifiedStep = (viewport: any, width: number, height: number) => {
    // 计算每个轴的像素密度
    const xPixelsPerUnit = width / (viewport.xMax - viewport.xMin);
    const yPixelsPerUnit = height / (viewport.yMax - viewport.yMin);

    // 使用较小的像素密度确保1:1比例
    const pixelsPerUnit = Math.min(xPixelsPerUnit, yPixelsPerUnit);

    // 目标：每80像素一个主刻度
    const targetPixelSpacing = 80;
    const targetUnitSpacing = targetPixelSpacing / pixelsPerUnit;

    // 计算合适的步长
    const magnitude = Math.pow(10, Math.floor(Math.log10(targetUnitSpacing)));
    const normalized = targetUnitSpacing / magnitude;

    let step;
    if (normalized <= 1) step = magnitude;
    else if (normalized <= 2) step = 2 * magnitude;
    else if (normalized <= 5) step = 5 * magnitude;
    else step = 10 * magnitude;

    return step;
  };

  // 绘制笛卡尔网格
  const drawCartesianGrid = (ctx: CanvasRenderingContext2D, width: number, height: number, currentViewport: any, xStep: number, yStep: number, style: { color: string, lineWidth: number }) => {
    ctx.strokeStyle = style.color;
    ctx.lineWidth = style.lineWidth;
    ctx.beginPath();

    // 垂直线
    for (let x = Math.ceil(currentViewport.xMin / xStep) * xStep; x <= currentViewport.xMax; x += xStep) {
      const canvasX = toCanvasX(x, width, currentViewport);
      ctx.moveTo(canvasX, 0);
      ctx.lineTo(canvasX, height);
    }

    // 水平线
    for (let y = Math.ceil(currentViewport.yMin / yStep) * yStep; y <= currentViewport.yMax; y += yStep) {
      const canvasY = toCanvasY(y, height, currentViewport);
      ctx.moveTo(0, canvasY);
      ctx.lineTo(width, canvasY);
    }

    ctx.stroke();
  };

  // 绘制不同类型的网格 - 使用统一步长
  const drawGrid = (ctx: CanvasRenderingContext2D, width: number, height: number, currentViewport: any) => {
    // 使用统一步长确保1:1比例
    const step = getUnifiedStep(currentViewport, width, height);

    switch (gridType) {
      case 'major':
        drawCartesianGrid(ctx, width, height, currentViewport, step, step, {
          color: 'rgba(0, 0, 0, 0.15)',
          lineWidth: 0.5
        });
        break;

      case 'minor':
        // 主网格
        drawCartesianGrid(ctx, width, height, currentViewport, step, step, {
          color: 'rgba(0, 0, 0, 0.15)',
          lineWidth: 0.5
        });
        // 次网格 - 使用相同的细分比例
        drawCartesianGrid(ctx, width, height, currentViewport, step / 5, step / 5, {
          color: 'rgba(0, 0, 0, 0.08)',
          lineWidth: 0.25
        });
        break;

      case 'polar':
        drawPolarGrid(ctx, width, height, currentViewport, step);
        break;

      case 'isometric':
        drawIsometricGrid(ctx, width, height, currentViewport, step);
        break;

      case 'dots':
        drawDotGrid(ctx, width, height, currentViewport, step, step);
        break;

      default:
        break;
    }
  };

  // 绘制极坐标网格 - 使用统一步长
  const drawPolarGrid = (ctx: CanvasRenderingContext2D, width: number, height: number, currentViewport: any, step: number) => {
    const centerX = toCanvasX(0, width, currentViewport);
    const centerY = toCanvasY(0, height, currentViewport);
    const maxRadius = Math.max(
      Math.abs(currentViewport.xMax), Math.abs(currentViewport.xMin),
      Math.abs(currentViewport.yMax), Math.abs(currentViewport.yMin)
    );

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.12)';
    ctx.lineWidth = 0.5;

    // 绘制同心圆 - 使用统一步长
    const pixelsPerUnit = Math.min(
      width / (currentViewport.xMax - currentViewport.xMin),
      height / (currentViewport.yMax - currentViewport.yMin)
    );

    for (let r = step; r <= maxRadius; r += step) {
      const canvasR = r * pixelsPerUnit;
      if (canvasR > 0 && canvasR < Math.max(width, height)) {
        ctx.beginPath();
        ctx.arc(centerX, centerY, canvasR, 0, 2 * Math.PI);
        ctx.stroke();
      }
    }

    // 绘制径向线
    const angleStep = Math.PI / 12; // 15度间隔
    for (let angle = 0; angle < 2 * Math.PI; angle += angleStep) {
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      const endX = centerX + Math.cos(angle) * Math.max(width, height);
      const endY = centerY - Math.sin(angle) * Math.max(width, height);
      ctx.lineTo(endX, endY);
      ctx.stroke();
    }
  };

  // 绘制等距网格 - 使用统一步长
  const drawIsometricGrid = (ctx: CanvasRenderingContext2D, width: number, height: number, currentViewport: any, step: number) => {
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.12)';
    ctx.lineWidth = 0.5;

    // 转换为像素步长
    const pixelsPerUnit = Math.min(
      width / (currentViewport.xMax - currentViewport.xMin),
      height / (currentViewport.yMax - currentViewport.yMin)
    );
    const pixelStep = step * pixelsPerUnit;

    const angle1 = Math.PI / 6; // 30度
    const angle2 = -Math.PI / 6; // -30度

    // 绘制第一组平行线
    for (let i = -width; i < width + height; i += pixelStep) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + height * Math.tan(angle1), height);
      ctx.stroke();
    }

    // 绘制第二组平行线
    for (let i = -width; i < width + height; i += pixelStep) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + height * Math.tan(angle2), height);
      ctx.stroke();
    }

    // 绘制水平线
    for (let y = 0; y <= height; y += pixelStep) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  };

  // 绘制点状网格
  const drawDotGrid = (ctx: CanvasRenderingContext2D, width: number, height: number, currentViewport: any, xStep: number, yStep: number) => {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';

    for (let x = Math.ceil(currentViewport.xMin / xStep) * xStep; x <= currentViewport.xMax; x += xStep) {
      for (let y = Math.ceil(currentViewport.yMin / yStep) * yStep; y <= currentViewport.yMax; y += yStep) {
        const canvasX = toCanvasX(x, width, currentViewport);
        const canvasY = toCanvasY(y, height, currentViewport);

        if (canvasX >= 0 && canvasX <= width && canvasY >= 0 && canvasY <= height) {
          ctx.beginPath();
          ctx.arc(canvasX, canvasY, 1.5, 0, 2 * Math.PI);
          ctx.fill();
        }
      }
    }
  };

  // 绘制坐标轴 - 使用统一步长
  const drawAxes = (ctx: CanvasRenderingContext2D, width: number, height: number, currentViewport: any) => {
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;

    const xAxisY = Math.max(0, Math.min(height, toCanvasY(0, height, currentViewport)));
    const yAxisX = Math.max(0, Math.min(width, toCanvasX(0, width, currentViewport)));

    // X轴
    ctx.beginPath();
    ctx.moveTo(0, xAxisY);
    ctx.lineTo(width, xAxisY);
    ctx.stroke();

    // Y轴
    ctx.beginPath();
    ctx.moveTo(yAxisX, 0);
    ctx.lineTo(yAxisX, height);
    ctx.stroke();

    // 绘制刻度标签 - 使用统一步长
    ctx.fillStyle = '#333';
    ctx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

    const formatNumber = (num: number): string => {
      if (Math.abs(num) < 0.001) return '0';
      if (Math.abs(num) >= 10000) return num.toExponential(0);
      if (Math.abs(num) >= 1000) return num.toFixed(0);
      if (Math.abs(num) < 0.01) return num.toExponential(1);
      if (Math.abs(num) < 1) return num.toFixed(2);
      return num.toFixed(1).replace(/\.0$/, '');
    };

    // 使用统一步长
    const step = getUnifiedStep(currentViewport, width, height);

    // X轴标签
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    for (let x = Math.ceil(currentViewport.xMin / step) * step; x <= currentViewport.xMax; x += step) {
      if (Math.abs(x) > step * 0.01) {
        const canvasX = toCanvasX(x, width, currentViewport);
        if (canvasX >= 0 && canvasX <= width) {
          ctx.fillText(formatNumber(x), canvasX, xAxisY + 8);
        }
      }
    }

    // Y轴标签
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let y = Math.ceil(currentViewport.yMin / step) * step; y <= currentViewport.yMax; y += step) {
      if (Math.abs(y) > step * 0.01) {
        const canvasY = toCanvasY(y, height, currentViewport);
        if (canvasY >= 0 && canvasY <= height) {
          ctx.fillText(formatNumber(y), yAxisX - 8, canvasY);
        }
      }
    }
  };

  // 绘制函数曲线 - 确保渲染整个画布范围
 const drawFunction = (ctx: CanvasRenderingContext2D, width: number, height: number, currentViewport: any, func: FunctionData) => {
   if (!func.points || func.points.length < 2) return;

   // 检查是否是不等式（有填充区域）
   const hasFillRegion = func.points.some(p => p.x === -Infinity && p.y === -Infinity);

   if (hasFillRegion) {
     // 绘制不等式区域
     drawInequalityFunction(ctx, width, height, currentViewport, func);
   } else {
     // 绘制普通函数
     drawNormalFunction(ctx, width, height, currentViewport, func);
   }
 };

 const drawInequalityFunction = (ctx: CanvasRenderingContext2D, width: number, height: number, currentViewport: any, func: FunctionData) => {
   // 分离填充点和边界点
   let fillPoints: Point[] = [];
   let boundarySegments: Point[][] = [];
   let inFillRegion = false;
   let currentBoundary: Point[] = [];

   for (const point of func.points) {
     // 填充区域标记
     if (point.x === -Infinity && point.y === -Infinity) {
       inFillRegion = true;
       continue;
     }
     if (point.x === Infinity && point.y === Infinity) {
       inFillRegion = false;
       continue;
     }

     // NaN 是段分隔符
     if (!isFinite(point.x) || !isFinite(point.y)) {
       if (currentBoundary.length > 0) {
         boundarySegments.push(currentBoundary);
         currentBoundary = [];
       }
       continue;
     }

     if (inFillRegion) {
       fillPoints.push(point);
     } else {
       currentBoundary.push(point);
     }
   }

   // 添加最后一段边界
   if (currentBoundary.length > 0) {
     boundarySegments.push(currentBoundary);
   }

   // 1. 绘制填充区域
   if (fillPoints.length > 0) {
     ctx.fillStyle = func.color + '20'; // 20% 透明度

     for (const point of fillPoints) {
       const x = toCanvasX(point.x, width, currentViewport);
       const y = toCanvasY(point.y, height, currentViewport);

       if (x >= 0 && x <= width && y >= 0 && y <= height) {
         ctx.fillRect(x - 1, y - 1, 2, 2); // 绘制小方块
       }
     }
   }

   // 2. 绘制边界（虚线）
   if (boundarySegments.length > 0) {
     ctx.strokeStyle = func.color;
     ctx.lineWidth = 2.5;
     ctx.setLineDash([8, 4]); // 虚线样式
     ctx.lineCap = 'round';
     ctx.lineJoin = 'round';

     for (const segment of boundarySegments) {
       if (segment.length < 2) continue;

       ctx.beginPath();
       let isDrawing = false;

       for (const point of segment) {
         const x = toCanvasX(point.x, width, currentViewport);
         const y = toCanvasY(point.y, height, currentViewport);

         // 扩大边界检查范围
         const margin = 50;
         if (x >= -margin && x <= width + margin &&
             y >= -margin && y <= height + margin) {
           if (!isDrawing) {
             ctx.moveTo(x, y);
             isDrawing = true;
           } else {
             ctx.lineTo(x, y);
           }
         } else {
           // 如果点在边界外，结束当前路径
           if (isDrawing) {
             ctx.stroke();
             ctx.beginPath();
             isDrawing = false;
           }
         }
       }

       if (isDrawing) {
         ctx.stroke();
       }
     }

     ctx.setLineDash([]); // 重置为实线
   }
 };

 const drawNormalFunction = (ctx: CanvasRenderingContext2D, width: number, height: number, currentViewport: any, func: FunctionData) => {
   ctx.strokeStyle = func.color;
   ctx.lineWidth = 2.5;
   ctx.lineCap = 'round';
   ctx.lineJoin = 'round';

   // 将点分段（通过 NaN 分隔）
   const segments: Point[][] = [];
   let currentSegment: Point[] = [];

   for (const point of func.points) {
     if (!isFinite(point.x) || !isFinite(point.y)) {
       if (currentSegment.length > 0) {
         segments.push(currentSegment);
         currentSegment = [];
       }
     } else {
       currentSegment.push(point);
     }
   }

   if (currentSegment.length > 0) {
     segments.push(currentSegment);
   }

   // 绘制每个段
   for (const segment of segments) {
     if (segment.length < 2) continue;

     ctx.beginPath();
     let isDrawing = false;

     for (let i = 0; i < segment.length; i++) {
       const point = segment[i];
       const x = toCanvasX(point.x, width, currentViewport);
       const y = toCanvasY(point.y, height, currentViewport);

       const margin = 100;
       const inBounds = x >= -margin && x <= width + margin &&
                       y >= -margin && y <= height + margin;

       if (!inBounds) {
         isDrawing = false;
         continue;
       }

       if (!isDrawing) {
         ctx.moveTo(x, y);
         isDrawing = true;
       } else {
         ctx.lineTo(x, y);
       }
     }

     ctx.stroke();
   }
 };

  // 修复触摸事件处理
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const passiveOptions = { passive: false };

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();

      if (e.touches.length === 1) {
        setIsDragging(true);
        setDragStart({
          x: e.touches[0].clientX,
          y: e.touches[0].clientY
        });
        setLastViewport(viewport);
      } else if (e.touches.length === 2) {
        const distance = getTouchDistance(e.touches);
        setTouchDistance(distance);
        setLastViewport(viewport);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();

      const rect = canvas.getBoundingClientRect();

      if (e.touches.length === 1 && isDragging) {
        const deltaX = e.touches[0].clientX - dragStart.x;
        const deltaY = e.touches[0].clientY - dragStart.y;

        const xRange = viewport.xMax - viewport.xMin;
        const yRange = viewport.yMax - viewport.yMin;

        const newViewport = {
          xMin: lastViewport.xMin - (deltaX / rect.width) * xRange,
          xMax: lastViewport.xMax - (deltaX / rect.width) * xRange,
          yMin: lastViewport.yMin + (deltaY / rect.height) * yRange,
          yMax: lastViewport.yMax + (deltaY / rect.height) * yRange,
        };

        onViewportChange(newViewport);
      } else if (e.touches.length === 2) {
        const newDistance = getTouchDistance(e.touches);
        if (touchDistance > 0) {
          const scale = newDistance / touchDistance;
          const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

          const mathX = toMathX(centerX - rect.left, rect.width, viewport);
          const mathY = toMathY(centerY - rect.top, rect.height, viewport);

          const zoomFactor = 1 / scale;

          const newViewport = {
            xMin: mathX + (lastViewport.xMin - mathX) * zoomFactor,
            xMax: mathX + (lastViewport.xMax - mathX) * zoomFactor,
            yMin: mathY + (lastViewport.yMin - mathY) * zoomFactor,
            yMax: mathY + (lastViewport.yMax - mathY) * zoomFactor,
          };

          onViewportChange(newViewport);
          setTouchDistance(newDistance);
          setLastViewport(newViewport);
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      setIsDragging(false);
      setTouchDistance(0);
    };

    canvas.addEventListener('touchstart', handleTouchStart, passiveOptions);
    canvas.addEventListener('touchmove', handleTouchMove, passiveOptions);
    canvas.addEventListener('touchend', handleTouchEnd, passiveOptions);

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [viewport, isDragging, dragStart, lastViewport, touchDistance, onViewportChange]);

  // 获取触摸距离
  const getTouchDistance = (touches: TouchList) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // 鼠标事件处理
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const mathX = toMathX(mouseX, rect.width, viewport);
    const mathY = toMathY(mouseY, rect.height, viewport);

    const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9;

    const newViewport = {
      xMin: mathX + (viewport.xMin - mathX) * zoomFactor,
      xMax: mathX + (viewport.xMax - mathX) * zoomFactor,
      yMin: mathY + (viewport.yMin - mathY) * zoomFactor,
      yMax: mathY + (viewport.yMax - mathY) * zoomFactor,
    };

    onViewportChange(newViewport);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setLastViewport(viewport);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;

    const xRange = viewport.xMax - viewport.xMin;
    const yRange = viewport.yMax - viewport.yMin;

    const newViewport = {
      xMin: lastViewport.xMin - (deltaX / rect.width) * xRange,
      xMax: lastViewport.xMax - (deltaX / rect.width) * xRange,
      yMin: lastViewport.yMin + (deltaY / rect.height) * yRange,
      yMax: lastViewport.yMax + (deltaY / rect.height) * yRange,
    };

    onViewportChange(newViewport);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div ref={containerRef} className="canvas-container">
      <canvas
        ref={canvasRef}
        className="graph-canvas"
        style={{ touchAction: 'none' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
    </div>
  );
};

// 函数列表组件 - 保持原有功能
const FunctionList: React.FC<{
  functions: FunctionData[],
  onToggleVisibility: (id: string) => void,
  onRemoveFunction: (id: string) => void,
  onChangeColor: (id: string, color: string) => void
}> = ({ functions, onToggleVisibility, onRemoveFunction, onChangeColor }) => {
  const language = getLanguage();

  if (functions.length === 0) return null;

  return (
    <div className="function-list">
      <h4>{t('functionListTitle', language)} ({functions.length})</h4>
      {functions.map((func, index) => (
        <div key={func.id} className="function-item">
          <div className="function-controls">
            <button
              className={`visibility-btn ${func.visible ? 'visible' : 'hidden'}`}
              onClick={() => onToggleVisibility(func.id)}
              title={func.visible ? t('hideFunction', language) : t('showFunction', language)}
            >
              <Icon icon={func.visible ? Icons.Eye : Icons.EyeOff} size={16} />
            </button>

            <input
              type="color"
              value={func.color}
              onChange={(e) => onChangeColor(func.id, e.target.value)}
              className="color-picker"
              title={t('changeColor', language)}
            />

            <button
              className="remove-btn"
              onClick={() => onRemoveFunction(func.id)}
              title={t('removeFunction', language)}
            >
              <Icon icon={Icons.X} size={16} />
            </button>
          </div>

          <div className="function-info">
            <LatexExpression expression={func.expression} className="function-expression" style={{ color: func.color }} />
            <div className="function-stats">
              {t('pointsCount', language).replace('{count}', func.points.length.toString())}
              {func.discontinuities && func.discontinuities.length > 0 &&
                `, ${t('discontinuitiesCount', language).replace('{count}', func.discontinuities.length.toString())}`
              }
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// 主应用组件
const GraphCalculator: React.FC = () => {
  const [expressions, setExpressions] = useState(['y = sin(x)']);
  const [currentExpression, setCurrentExpression] = useState('');
  const [viewport, setViewport] = useState({
    xMin: -10,
    xMax: 10,
    yMin: -10,
    yMax: 10
  });
  const [functions, setFunctions] = useState<FunctionData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [gridType, setGridType] = useState<GridType>('major');
  const [showExamples, setShowExamples] = useState(false);
  const [showGridMenu, setShowGridMenu] = useState(false);
  const [showFunctionList, setShowFunctionList] = useState(false);
  const [language, setLanguage] = useState<Language>(getLanguage());
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // 防抖相关状态
  const [debouncedExpressions, setDebouncedExpressions] = useState(expressions);
  const [debouncedViewport, setDebouncedViewport] = useState(viewport);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewportDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setCanvasSize = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const size = Math.max(w, h);
    document.documentElement.style.setProperty('--canvas-size', `${size}px`);
  };

  useEffect(() => {
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);
    return () => window.removeEventListener('resize', setCanvasSize);
  }, []);

  // 表达式防抖
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedExpressions(expressions);
    }, 800);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [expressions]);

  // 视口防抖
  useEffect(() => {
    if (viewportDebounceTimerRef.current) {
      clearTimeout(viewportDebounceTimerRef.current);
    }

    viewportDebounceTimerRef.current = setTimeout(() => {
      setDebouncedViewport(viewport);
    }, 300);

    return () => {
      if (viewportDebounceTimerRef.current) {
        clearTimeout(viewportDebounceTimerRef.current);
      }
    };
  }, [viewport]);

  // 示例函数集合
  const exampleCategories = [
    {
      category: t('basicFunctions', language),
      examples: [
        { name: t('line', language), expr: 'y = 2*x + 1' },
        { name: t('parabola', language), expr: 'y = x^2 - 4*x + 3' },
        { name: t('cubic', language), expr: 'y = x^3 - 3*x' },
        { name: t('rational', language), expr: 'y = 1/(x^2 + 1)' }
      ]
    },
    {
      category: t('trigonometric', language),
      examples: [
        { name: t('sineWave', language), expr: 'y = sin(x)' },
        { name: t('cosineWave', language), expr: 'y = cos(x)' },
        { name: t('tangent', language), expr: 'y = tan(x)' },
        { name: t('modulatedWave', language), expr: 'y = sin(x) * cos(10*x)' },
        { name: t('lissajous', language), expr: 'y = sin(3*x + pi/4)' },
        { name: t('dampedOscillation', language), expr: 'y = exp(-x/5) * sin(2*x)' }
      ]
    },
    {
      category: t('multiFunctionExamples', language),
      examples: [
        { name: t('sineAndCosine', language), expr: 'y = sin(x)\ny = cos(x)' },
        { name: t('parabolas', language), expr: 'y = x^2\ny = -x^2 + 4' },
        { name: t('exponentials', language), expr: 'y = exp(x)\ny = exp(-x)' },
        { name: t('inverseFunctions', language), expr: 'y = x\ny = 1/x' }
      ]
    },
    {
      category: t('advancedEquations', language),
      examples: [
        { name: t('reverseSine', language), expr: 'sin(x) = y' },
        { name: t('complexReverse', language), expr: 'x^2 + y^2 = 25' },
        { name: t('parametricStyle', language), expr: 'cos(t) = x\nsin(t) = y' }
      ]
    },
{
    category: t('implicitEquations', language),
    examples: [
      { name: t('circle', language), expr: 'x^2 + y^2 = 25' },
      { name: t('ellipse', language), expr: 'x^2/16 + y^2/9 = 1' },
      { name: t('hyperbola', language), expr: 'x^2/9 - y^2/4 = 1' },
      { name: t('lemniscate', language), expr: '(x^2 + y^2)^2 = 25*(x^2 - y^2)' },
      { name: t('foliumOfDescartes', language), expr: 'x^3 + y^3 = 3*x*y' }
    ]
  },
  {
    category: t('parametricCurves', language),
    examples: [
      { name: t('lissajousCurve', language), expr: 'x = sin(3*t)\ny = sin(2*t)' },
      { name: t('spiral', language), expr: 'x = t*cos(t)\ny = t*sin(t)' },
      { name: t('cycloid', language), expr: 'x = t - sin(t)\ny = 1 - cos(t)' },
      { name: t('astroid', language), expr: 'x = cos(t)^3\ny = sin(t)^3' },
      { name: t('epicycloid', language), expr: 'x = 5*cos(t) - cos(5*t)\ny = 5*sin(t) - sin(5*t)' }
    ]
  },
  {
    category: t('polarCurves', language),
    examples: [
      { name: t('rose', language), expr: 'r = sin(4*theta)' },
      { name: t('cardioid', language), expr: 'r = 1 - cos(theta)' },
      { name: t('archimedeanSpiral', language), expr: 'r = theta' },
      { name: t('logarithmicSpiral', language), expr: 'r = exp(0.1*theta)' },
      { name: t('butterfly', language), expr: 'r = exp(sin(theta)) - 2*cos(4*theta)' }
    ]
  },
  {
    category: t('inequalities', language),
    examples: [
      { name: t('halfPlane', language), expr: 'y > 2*x + 1' },
      { name: t('parabolicRegion', language), expr: 'y < x^2' },
      { name: t('circularRegion', language), expr: 'x^2 + y^2 <= 16' },
      { name: t('absoluteRegion', language), expr: 'abs(x) + abs(y) <= 4' },
      { name: t('systemOfInequalities', language), expr: 'y > x^2\ny < 2*x + 3' }
    ]
  }
  ];

  // 计算最佳视口
  const calculateOptimalViewport = useCallback((funcs: FunctionData[]) => {
    const visibleFunctions = funcs.filter(f => f.visible && f.points.length > 0);

    if (visibleFunctions.length === 0) {
      return {
        xMin: -10,
        xMax: 10,
        yMin: -10,
        yMax: 10
      };
    }

    const allPoints: Point[] = [];
    visibleFunctions.forEach(func => {
      const validPoints = func.points.filter(p =>
        isFinite(p.x) && isFinite(p.y) &&
        !isNaN(p.x) && !isNaN(p.y) &&
        Math.abs(p.y) < 1e6
      );
      allPoints.push(...validPoints);
    });

    if (allPoints.length === 0) {
      return {
        xMin: -10,
        xMax: 10,
        yMin: -10,
        yMax: 10
      };
    }

    const xValues = allPoints.map(p => p.x);
    const yValues = allPoints.map(p => p.y);

    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);

    let xRange = xMax - xMin;
    let yRange = yMax - yMin;

    const minRange = 1;
    if (xRange < minRange) xRange = minRange;
    if (yRange < minRange) yRange = minRange;

    const xMargin = xRange * 0.2;
    const yMargin = yRange * 0.2;

    return {
      xMin: xMin - xMargin,
      xMax: xMax + xMargin,
      yMin: yMin - yMargin,
      yMax: yMax + yMargin
    };
  }, []);

  // 获取图形数据
  const fetchGraphData = useCallback(async (exprs: string[], vp: typeof viewport) => {
    const validExpressions = exprs.filter(expr => expr.trim());
    if (validExpressions.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const request: GraphRequest = {
        expressions: validExpressions,
        viewport: vp,
        resolution: 2000,
        type: 'cartesian'
      };

      const response = await fetch('/api/graph/parse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request)
      });

      const data: GraphResponse = await response.json();

      if (data.success && data.data) {
        setFunctions(data.data.functions);
        console.log('Graph data received:', data.data.functions.length, 'functions');
      } else {
        setError(data.error || t('unknownError', language));
        console.error('Graph error:', data.error);
      }
    } catch (err) {
      setError(t('connectionError', language));
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [language]);

  // 监听防抖后的表达式变化
  useEffect(() => {
    fetchGraphData(debouncedExpressions, debouncedViewport);
  }, [debouncedExpressions, debouncedViewport, fetchGraphData]);

  // 添加新表达式
  const addExpression = () => {
    if (currentExpression.trim()) {
      const newExpressions = splitInputExpressions(currentExpression);
      setExpressions(prev => [...prev, ...newExpressions]);
      setCurrentExpression('');
    }
  };

  // 处理回车键
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addExpression();
    }
  };

  // 智能复位视口
  const resetViewport = useCallback(() => {
    // 1. 收集所有可见函数的所有有效点
    const visibleFunctions = functions.filter(f => f.visible && f.points.length > 0);
    let allPoints: Point[] = [];
    visibleFunctions.forEach(func => {
      const validPoints = func.points.filter(p =>
        isFinite(p.x) && isFinite(p.y) &&
        !isNaN(p.x) && !isNaN(p.y) &&
        Math.abs(p.y) < 1e6
      );
      allPoints.push(...validPoints);
    });

    if (allPoints.length === 0) {
      setViewport({ xMin: -10, xMax: 10, yMin: -10, yMax: 10 });
      return;
    }

    // 2. 计算所有点的中位数点
    const sortedX = allPoints.map(p => p.x).sort((a, b) => a - b);
    const sortedY = allPoints.map(p => p.y).sort((a, b) => a - b);
    const midIndex = Math.floor(sortedX.length / 2);
    const centerX = sortedX[midIndex];
    const centerY = sortedY[midIndex];

    // 3. 计算当前视口范围
    const xRange = viewport.xMax - viewport.xMin;
    const yRange = viewport.yMax - viewport.yMin;

    // 4. 计算平移后的新视口
    const newViewport = {
      xMin: centerX - xRange / 2,
      xMax: centerX + xRange / 2,
      yMin: centerY - yRange / 2,
      yMax: centerY + yRange / 2
    };

    setViewport(newViewport);
  }, [functions, viewport]);

  // 函数管理
  const toggleFunctionVisibility = (id: string) => {
    setFunctions(prev => prev.map(func =>
      func.id === id ? { ...func, visible: !func.visible } : func
    ));
  };

  const removeFunctionById = (id: string) => {
    setFunctions(prev => prev.filter(func => func.id !== id));
    const funcIndex = functions.findIndex(f => f.id === id);
    if (funcIndex >= 0) {
      setExpressions(prev => prev.filter((_, index) => index !== funcIndex));
    }
  };

  const changeFunctionColor = (id: string, color: string) => {
    setFunctions(prev => prev.map(func =>
      func.id === id ? { ...func, color } : func
    ));
  };

  // 导出图片功能
  const exportAsImage = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.toBlob((blob) => {
      if (blob) {
        // 1. 传统下载方式
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `graph_${Date.now()}.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);

        // 2. iOS WKWebView 回传，便于原生保存/分享
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.graphExport) {
          window.webkit.messageHandlers.graphExport.postMessage({
            image: canvas.toDataURL('image/png'),
            expressions: functions.map(f => f.expression),
            viewport: viewport
          });
        }
      }
    });
  };

  const handleCanvasRef = (canvas: HTMLCanvasElement) => {
    canvasRef.current = canvas;
  };

  const gridOptions = [
    { value: 'none', label: t('gridNone', language), icon: Icons.Square },
    { value: 'major', label: t('gridMajor', language), icon: Icons.Grid3x3 },
    { value: 'minor', label: t('gridMinor', language), icon: Icons.Grid3x3 },
    { value: 'polar', label: t('gridPolar', language), icon: Icons.Target },
    { value: 'isometric', label: t('gridIsometric', language), icon: Icons.Box },
    { value: 'dots', label: t('gridDots', language), icon: Icons.MoreHorizontal }
  ];

  // 简化的按钮处理函数
  const handleButtonClick = (buttonType: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    console.log('Button clicked:', buttonType);

    switch (buttonType) {
      case 'functionList':
        setShowFunctionList(!showFunctionList);
        break;
      case 'examples':
        setShowExamples(!showExamples);
        break;
      case 'reset':
        resetViewport();
        break;
      case 'export':
        exportAsImage();
        break;
      case 'help':
        setShowHelp(!showHelp);
        break;
      default:
        console.log('Unknown button type:', buttonType);
    }
  };

  return (
    <div className="graph-calculator">
      {/* 画布区域 */}
      <div className="canvas-area">
        <GraphCanvas
          functions={functions}
          viewport={viewport}
          onViewportChange={setViewport}
          onExportRequest={handleCanvasRef}
          gridType={gridType}
        />
      </div>

      {/* UI层 */}
      <div className="ui-layer">
        {/* 左上角 Logo */}
        <a
          href="https://endlessai.org"
          className="logo-fab"
          target="_blank"
          rel="noopener noreferrer"
          title="EndlessAI"
        >
          <img src="/logo.png" alt="EndlessAI Logo" className="logo-img" />
        </a>

        {/* 右上角：控制按钮 - 简化版本 */}
        <div className="control-buttons">
          {/* 1. 当前方程式列表 - 简化处理 */}
          <button
            onClick={(e) => handleButtonClick('functionList', e)}
            className={`control-btn ${functions.length > 0 ? 'active' : ''}`}
            title={t('functionList', language)}
            type="button"
          >
            <Icon icon={Icons.List} />
            {functions.length > 0 && <span className="function-count">{functions.length}</span>}
          </button>

          {/* 2. 示例函数 - 简化处理 */}
          <button
            onClick={(e) => handleButtonClick('examples', e)}
            className="control-btn"
            title={t('exampleFunctions', language)}
            type="button"
          >
            <Icon icon={Icons.Activity} />
          </button>

         {/* 3. 定位按钮 - 简化处理 暂时不用

          <button
                              onClick={(e) => handleButtonClick('reset', e)}
                              className="control-btn"
                              title={t('resetView', language)}
                              type="button"
                            >
                              <Icon icon={Icons.Target} />
                            </button>

         */}

          {/* 4. 导出保存按钮 - 简化处理 */}
          <button
            onClick={(e) => handleButtonClick('export', e)}
            className="control-btn"
            title={t('exportImage', language)}
            type="button"
          >
            <Icon icon={Icons.Download} />
          </button>

          {/* 5. 网格背景选择 - 保持原样，因为它能工作 */}
          <div className="grid-menu-container">
            <button
              onClick={() => setShowGridMenu(!showGridMenu)}
              className={`control-btn ${gridType !== 'none' ? 'active' : ''}`}
              title={t('gridOptions', language)}
              type="button"
            >
              <Icon icon={Icons.Grid3x3} />
            </button>

            {showGridMenu && (
              <div className="grid-menu">
                {gridOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setGridType(option.value as GridType);
                      setShowGridMenu(false);
                    }}
                    className={`grid-option ${gridType === option.value ? 'active' : ''}`}
                    type="button"
                  >
                    <Icon icon={option.icon} size={16} />
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 6. 帮助按钮 - 简化处理 */}
          <button
            onClick={(e) => handleButtonClick('help', e)}
            className="control-btn"
            title={t('help', language)}
            type="button"
          >
            <Icon icon={Icons.HelpCircle} />
          </button>
        </div>

        {/* 底部：表达式输入 */}
        <div className="expression-bar">
          <div className="expression-input-container">
            <textarea
              value={currentExpression}
              onChange={(e) => setCurrentExpression(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('inputPlaceholder', language)}
              className="expression-input multi-line"
              rows={1}
            />
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addExpression();
              }}
              className="add-btn circular"
              disabled={!currentExpression.trim()}
              type="button"
            >
              <Icon icon={Icons.Plus} size={20} />
            </button>
          </div>
          {error && <div className="error-tooltip">{error}</div>}
        </div>

        {/* 加载提示 */}
        {loading && <div className="loading-overlay">{t('calculating', language)}</div>}

        {/* 函数列表面板 */}
        {showFunctionList && (
          <div className="function-list-panel">
            <div className="panel-header">
              <h3><Icon icon={Icons.List} size={18} /> {t('functionList', language)}</h3>
              <button
                onClick={() => setShowFunctionList(false)}
                className="close-btn"
                type="button"
              >
                <Icon icon={Icons.X} size={20} />
              </button>
            </div>
            <div className="panel-content">
              <FunctionList
                functions={functions}
                onToggleVisibility={toggleFunctionVisibility}
                onRemoveFunction={removeFunctionById}
                onChangeColor={changeFunctionColor}
              />
            </div>
          </div>
        )}

        {/* 示例面板 */}
        {showExamples && (
          <div className="examples-panel">
            <div className="panel-header">
              <h3><Icon icon={Icons.Activity} size={18} /> {t('exampleFunctions', language)}</h3>
              <button
                onClick={() => setShowExamples(false)}
                className="close-btn"
                type="button"
              >
                <Icon icon={Icons.X} size={20} />
              </button>
            </div>

            <div className="examples-content">
              {exampleCategories.map((category) => (
                <div key={category.category} className="example-category">
                  <h4>{category.category}</h4>
                  <div className="example-list">
                    {category.examples.map((example) => (
                      <button
                        key={example.expr}
                        onClick={() => {
                          if (example.expr.includes('\n')) {
                            const newExpressions = splitInputExpressions(example.expr);
                            setExpressions(newExpressions);
                          } else {
                            setCurrentExpression(example.expr);
                          }
                          setShowExamples(false);
                        }}
                        className="example-item"
                        type="button"
                      >
                        <span className="example-name">{example.name}</span>
                        <LatexExpression expression={example.expr} className="example-expr" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 帮助面板 */}
        {showHelp && (
          <div className="help-panel">
            <div className="help-header">
              <h3><Icon icon={Icons.HelpCircle} size={18} /> {t('helpTitle', language)}</h3>
              <button
                onClick={() => setShowHelp(false)}
                className="close-btn"
                type="button"
              >
                <Icon icon={Icons.X} size={20} />
              </button>
            </div>

            <div className="help-content">
              <div className="help-section">
                <h4>{t('enhancedEquationSupport', language)}</h4>
                <p>{t('enhancedEquationSupportDesc', language)}</p>
                <ul>
                  <li><code>y = sin(x)</code> - {t('standardForm', language)}</li>
                  <li><code>sin(x) = y</code> - {t('reverseForm', language)} ✨</li>
                  <li><code>f(x) = x^2 + 2*x + 1</code> - {t('functionDefinition', language)}</li>
                </ul>
              </div>

              <div className="help-section">
                <h4>{t('multiFunctionSupport', language)}</h4>
                <ul>
                  <li>{t('addMultipleFunctions', language)}</li>
                  <li>{t('manageFunctions', language)}</li>
                  <li>{t('toggleVisibility', language)}</li>
                </ul>
              </div>

              <div className="help-section">
                <h4>{t('operations', language)}</h4>
                <ul>
                  <li>{t('dragToPan', language)}</li>
                  <li>{t('scrollToZoom', language)}</li>
                  <li>{t('mobilePinchZoom', language)}</li>
                  <li>{t('clickReset', language)}</li>
                </ul>
              </div>

              <div className="help-section">
                <h4>{t('supportedFunctions', language)}</h4>
                <div className="function-grid">
                  <div>
                    <h5>{t('basicOperations', language)}</h5>
                    <code>+ - * / ^ % mod</code>
                  </div>
                  <div>
                    <h5>{t('trigFunctions', language)}</h5>
                    <code>sin cos tan cot sec csc</code>
                  </div>
                  <div>
                    <h5>{t('inverseTrig', language)}</h5>
                    <code>asin acos atan acot asec acsc</code>
                  </div>
                  <div>
                    <h5>{t('hyperbolic', language)}</h5>
                    <code>sinh cosh tanh coth sech csch</code>
                  </div>
                  <div>
                    <h5>{t('exponentialLog', language)}</h5>
                    <code>exp ln log log10 log2 lg lb</code>
                  </div>
                  <div>
                    <h5>{t('rootsPowers', language)}</h5>
                    <code>sqrt cbrt root pow</code>
                  </div>
                  <div>
                    <h5>{t('rounding', language)}</h5>
                    <code>abs sign ceil floor round trunc min max clamp</code>
                  </div>
                  <div>
                    <h5>{t('special', language)}</h5>
                    <code>gamma erf erfc besselj sinc normal sigmoid relu</code>
                  </div>
                  <div>
                    <h5>{t('constants', language)}</h5>
                    <code>pi e phi tau</code>
                  </div>
                </div>
              </div>


{/* 声明部分 */}
                                        <div className="help-footer">
                                          <div className="help-footer-section">
                                            <p>
                                              {t('madeBy', language)}{' '}
                                              <a
                                                href="https://endlessai.org"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="help-link"
                                              >
                                                {t('endlessaiOrg', language)}
                                              </a>
                                            </p>
                                          </div>

                                          <div className="help-footer-section">
                                            <p>{t('feedbackWelcome', language)}</p>
                                            <div className="contact-info">
                                              <div className="contact-item">
                                                <span className="contact-label">{t('email', language)}:</span>
                                                <a
                                                  href="mailto:help@endlessai.org"
                                                  className="help-link"
                                                >
                                                  help@endlessai.org
                                                </a>
                                              </div>
                                              <div className="contact-item">
                                                <span className="contact-label">{t('wechat', language)}:</span>
                                                <span className="contact-value">iChenlidong</span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>



            </div>





          </div>



        )}
      </div>
    </div>
  );
};

// 渲染应用
const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(<GraphCalculator />);
